import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "forceOverhangs", "gradientColor", "Overhang", "size", "onScroll", "$cssStyles"];
import { Children, cloneElement, PureComponent, createRef } from 'react';
import shallowEqual from 'react-utils/shallowEqual';
import styled from 'foundations-theming/utils';
import { withCssStyles } from '../../../utils/withCssStyles/withCssStyles';
import { cancelSchedulerCallback, requestSchedulerCallback } from './ScrollOverhangUtils';
import { callIfPossible } from '../../utils/functions/functions';
import { Overhang } from '../Overhang/Overhang';
import { filterProps } from '../../../utils/filterProps';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const sides = ['top', 'bottom', 'left', 'right'];
const Wrapper = styled.div.withConfig({
  displayName: "ScrollOverhang__Wrapper",
  componentId: "sc-1r0keeu-0"
})(["position:relative;min-height:0;display:flex;flex:1 1 auto;", ""], ({
  $cssStyles
}) => $cssStyles);
export class ScrollOverhangBase extends PureComponent {
  constructor(...args) {
    //@ts-expect-error - super() is called this way in UIComponents
    super(...args);
    this.handleScroll = evt => {
      callIfPossible(this.props.onScroll, evt);
      this.delayedMeasureScrollableArea();
    };
    this.delayedMeasureScrollableArea = () => {
      if (this._timeout) {
        cancelSchedulerCallback(this._timeout); // Ensure only one callback is queued at a time
      }
      this._timeout = requestSchedulerCallback(this.measureScrollableArea);
    };
    this.measureScrollableArea = () => {
      const thisEl = this.wrapperRef.current;
      // Bail out if our only children are the 4 scroll overhang elements
      if (thisEl == null || thisEl.children.length === 4) return;
      const childEl = thisEl.children[0];
      const computedStyles = getComputedStyle(childEl, null);

      // Patch for Firefox < 62: https://bugzilla.mozilla.org/show_bug.cgi?id=1467722
      if (computedStyles == null) return;
      const {
        clientHeight,
        clientWidth,
        scrollHeight,
        scrollLeft,
        scrollTop,
        scrollWidth
      } = childEl;
      const isScrollable = overflow => overflow === 'auto' || overflow === 'scroll';
      const scrollsVertically = isScrollable(computedStyles.overflowY);
      const scrollsHorizontally = isScrollable(computedStyles.overflowX);
      const measuredScrollableArea = {
        top: scrollsVertically ? scrollTop : 0,
        bottom: scrollsVertically ? scrollHeight - clientHeight - scrollTop : 0,
        left: scrollsHorizontally ? scrollLeft : 0,
        right: scrollsHorizontally ? scrollWidth - clientWidth - scrollLeft : 0
      };
      if (!shallowEqual(measuredScrollableArea, this.state.measuredScrollableArea)) {
        this.setState({
          measuredScrollableArea
        });
      }
    };
    this.state = {
      measuredScrollableArea: {
        top: 0,
        bottom: 0,
        left: 0,
        right: 0
      }
    };
    this.wrapperRef = /*#__PURE__*/createRef();
  }
  componentDidMount() {
    this.delayedMeasureScrollableArea();
    const thisEl = this.wrapperRef.current;
    this.resizeObserver = new ResizeObserver(this.delayedMeasureScrollableArea);
    this.resizeObserver.observe(thisEl);
  }
  componentDidUpdate(_prevProps, prevState) {
    // ⚠️ This condition prevents infinite loops! ⚠️
    if (prevState.measuredScrollableArea === this.state.measuredScrollableArea) {
      this.delayedMeasureScrollableArea();
    }
    const thisEl = this.wrapperRef.current;
    this.resizeObserver = new ResizeObserver(this.delayedMeasureScrollableArea);
    this.resizeObserver.observe(thisEl);
  }
  componentWillUnmount() {
    const thisEl = this.wrapperRef.current;
    this.resizeObserver.unobserve(thisEl);
    if (this._timeout) {
      cancelSchedulerCallback(this._timeout);
    }
    if (this._frame) {
      cancelAnimationFrame(this._frame);
    }
  }
  getClonedChild(children, measuredScrollableArea) {
    const child = Children.only(children);
    const childTabIndex = child.props && child.props.tabIndex;
    const childIsScrollable = sides.some(side => measuredScrollableArea[side] > 0);
    const clonedElement = /*#__PURE__*/cloneElement(child, {
      tabIndex: childIsScrollable ? childTabIndex || 0 : childTabIndex,
      onScroll: this.handleScroll
    });
    return clonedElement;
  }
  render() {
    const _this$props = this.props,
      {
        children,
        forceOverhangs,
        gradientColor = 'fill-tertiary-default',
        Overhang: _Overhang = Overhang,
        size = 14,
        $cssStyles
      } = _this$props,
      rest = _objectWithoutPropertiesLoose(_this$props, _excluded);
    const {
      measuredScrollableArea
    } = this.state;
    const clonedChild = this.getClonedChild(children, measuredScrollableArea);
    const overhangProps = {
      gradientColor,
      size
    };

    // Fade smoothly from 1 (scrollable area exceeds overhang size) to 0 (no scrollable area)
    const getOpacity = side => {
      var _forceOverhangs$side;
      if (size === 0) {
        return 0;
      }
      return ((_forceOverhangs$side = forceOverhangs === null || forceOverhangs === void 0 ? void 0 : forceOverhangs[side]) !== null && _forceOverhangs$side !== void 0 ? _forceOverhangs$side : false) ? 1 : Math.min((measuredScrollableArea[side] || 0) / size, 1);
    };
    const {
      domProps
    } = filterProps(rest);
    return /*#__PURE__*/_jsxs(Wrapper, Object.assign({}, domProps, {
      $cssStyles: $cssStyles,
      ref: this.wrapperRef,
      children: [clonedChild, /*#__PURE__*/_jsx(_Overhang, Object.assign({}, overhangProps, {
        opacity: getOpacity('top'),
        side: "top"
      })), /*#__PURE__*/_jsx(_Overhang, Object.assign({}, overhangProps, {
        opacity: getOpacity('bottom'),
        side: "bottom"
      })), /*#__PURE__*/_jsx(_Overhang, Object.assign({}, overhangProps, {
        opacity: getOpacity('left'),
        side: "left"
      })), /*#__PURE__*/_jsx(_Overhang, Object.assign({}, overhangProps, {
        opacity: getOpacity('right'),
        side: "right"
      }))]
    }));
  }
}
export const ScrollOverhang = withCssStyles(ScrollOverhangBase);